import React, { useState, useEffect } from 'react';
import { 
  Server, 
  Database, 
  HardDrive, 
  Cpu, 
  Activity, 
  Settings, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Save
} from 'lucide-react';
import { adminSystemApi } from '../services/adminApi';

interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  uptime: string;
  cpuUsage: number;
  memoryUsage: number;
  diskUsage: number;
  activeConnections: number;
  lastCheck: string;
}

interface SystemConfig {
  id: string;
  name: string;
  value: string;
  description: string;
  category: 'general' | 'security' | 'performance' | 'integration' | 'financial' | 'system';
  editable: boolean;
}

interface SystemLog {
  id: string;
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  component: string;
  message: string;
  details?: string;
}

const AdminSystem: React.FC = () => {
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [configs, setConfigs] = useState<SystemConfig[]>([]);
  const [recentLogs, setRecentLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editingConfig, setEditingConfig] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    loadSystemData();
  }, []);

  const loadSystemData = async () => {
    try {
      setLoading(true);
      const [health, configsData, logsData] = await Promise.all([
        adminSystemApi.getSystemHealth(),
        adminSystemApi.getSystemConfigs(),
        adminSystemApi.getRecentLogs()
      ]);
      
      setSystemHealth(health as SystemHealth);
      setConfigs(configsData);
      setRecentLogs(logsData);
    } catch (error) {
      console.error('Failed to load system data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSystemData();
    setRefreshing(false);
  };

  const handleConfigEdit = (config: SystemConfig) => {
    setEditingConfig(config.id);
    setEditValue(config.value);
  };

  const handleConfigSave = async (configId: string) => {
    try {
      await adminSystemApi.updateSystemConfig(configId, editValue);
      setConfigs(prev => prev.map(config => 
        config.id === configId 
          ? { ...config, value: editValue }
          : config
      ));
      setEditingConfig(null);
      setEditValue('');
    } catch (error) {
      console.error('Failed to update config:', error);
    }
  };

  const handleConfigCancel = () => {
    setEditingConfig(null);
    setEditValue('');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'info':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'critical':
        return 'text-red-800 bg-red-100 border-red-300';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Management</h1>
          <p className="text-gray-600">Monitor system health and manage configurations</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* System Health */}
      {systemHealth && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">System Health</h2>
            <div className="flex items-center space-x-2">
              {getStatusIcon(systemHealth.status)}
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(systemHealth.status)}`}>
                {systemHealth.status.charAt(0).toUpperCase() + systemHealth.status.slice(1)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <Cpu className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-gray-600">CPU Usage</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-1">{systemHealth.cpuUsage}%</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <HardDrive className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-gray-600">Memory Usage</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-1">{systemHealth.memoryUsage}%</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <Database className="w-5 h-5 text-purple-600 mr-2" />
                <span className="text-sm font-medium text-gray-600">Disk Usage</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-1">{systemHealth.diskUsage}%</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <Activity className="w-5 h-5 text-orange-600 mr-2" />
                <span className="text-sm font-medium text-gray-600">Active Connections</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-1">{systemHealth.activeConnections}</p>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-500">
            <span>Uptime: {systemHealth.uptime}</span>
            <span className="mx-2">•</span>
            <span>Last Check: {systemHealth.lastCheck}</span>
          </div>
        </div>
      )}

      {/* System Configurations */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Configurations</h2>
        
        <div className="space-y-4">
          {configs.map((config) => (
            <div key={config.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="font-medium text-gray-900">{config.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      config.category === 'security' ? 'bg-red-100 text-red-800' :
                      config.category === 'performance' ? 'bg-blue-100 text-blue-800' :
                      config.category === 'integration' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {config.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{config.description}</p>
                  
                  {editingConfig === config.id ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => handleConfigSave(config.id)}
                        className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleConfigCancel}
                        className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                        {config.value}
                      </code>
                      {config.editable && (
                        <button
                          onClick={() => handleConfigEdit(config)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent System Logs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent System Logs</h2>
        
        <div className="space-y-3">
          {recentLogs.map((log) => (
            <div key={log.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getLogLevelColor(log.level)}`}>
                      {log.level.toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-500">{log.component}</span>
                    <span className="text-sm text-gray-400">{log.timestamp}</span>
                  </div>
                  <p className="text-gray-900 mb-1">{log.message}</p>
                  {log.details && (
                    <p className="text-sm text-gray-600">{log.details}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminSystem; 